import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// api
import ApiKey from 'Components/api/ApiKey';
import BaseApi from 'Components/api/BaseApi';
// components
import Sidebar from 'Components/employersSide/Sidebar';
import Footer from 'Components/element/Footer';
import NavBar from 'Components/element/NavBar';
// helpers
import { logOutIfTokenExpired, resultInfo } from 'Components/helpers';
// mui
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';

const FolderPage = () => {
   //   const { slug } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const { t } = useTranslation('global');
   const userType = Cookies.get('user_type');
   const tokenKey = Cookies.get('tokenClient');

   const params = new URLSearchParams(location.search);
   const folderId = params.get('folder_id');

   const [loading, setLoading] = useState(true);
   const [folderName, setFolderName] = useState('');
   const [currentPage, setCurrentPage] = useState(1);
   const [folderData, setFolderData] = useState(null);
   const [foldersList, setFoldersList] = useState(null);
   const [favouriteData, setFavouriteData] = useState([]);
   const [showFolderName, setShowFolderName] = useState(true);
   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
   const [selectedFolderId, setSelectedFolderId] = useState(null);

   const dataPerPage = 10;

   const indexOfLastRecord = currentPage * dataPerPage;
   const indexOfFirstRecord = indexOfLastRecord - dataPerPage;
   const currentData = favouriteData
      ? favouriteData?.slice(indexOfFirstRecord, indexOfLastRecord)
      : favouriteData?.slice(indexOfFirstRecord, indexOfLastRecord);

   const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

   const getFoldersList = async () => {
      try {
         const response = await axios.get(BaseApi + '/folder/list', {
            headers: {
               'Content-Type': 'application/json',
               key: ApiKey,
               token: tokenKey,
            },
         });

         if (response.status === 200) {
            setFoldersList(response.data);
         } else {
            resultInfo('error', response.data.message, t('jobseekerManageAlert.close'));
         }
      } catch (error) {
         console.error('Cannot get folders list', error);
      }
   };

   const getData = async () => {
      try {
         setLoading(true);

         const folderResponse = await axios.get(BaseApi + `/folder/${folderId}`, {
            headers: {
               key: ApiKey,
               token: tokenKey,
               contentType: 'application/json',
            },
         });

         const response = await axios.get(BaseApi + `/folder/cv/${folderId}`, {
            headers: {
               key: ApiKey,
               token: tokenKey,
               contentType: 'application/json',
            },
         });

         setLoading(false);

         if (response.status === 200) {
            setFavouriteData(response.data);
            setFolderData(folderResponse.data);
            setFolderName(folderResponse.data.name);
         } else if (response.status === 400) {
            navigate('/');

            Cookies.remove('fname');
            Cookies.remove('user_id');
            Cookies.remove('user_type');
            Cookies.remove('tokenClient');

            resultInfo('warning', response.data.message, t('employerFavouriteList.close'));
         } else {
            Swal.fire({
               icon: 'error',
               text: response.data.message,
               title: t('employerFavouriteList.failedTitle'),
               confirmButtonText: t('employerFavouriteList.close'),
            });
         }
      } catch (error) {
         setLoading(false);

         if (
            error.message === 'Network Error' ||
            error.response.data.message === 'Token has expired'
         ) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         }

         console.error(error);
      }
   };

   const handleChange = (e) => setFolderName(e.target.value);

   const handleFolderNameSave = async () => {
      try {
         setLoading(true);

         const response = await axios.put(
            BaseApi + `/folder/${folderId}`,
            { name: folderName },
            {
               headers: {
                  key: ApiKey,
                  token: tokenKey,
                  contentType: 'application/json',
               },
            }
         );

         setLoading(false);

         if (response.status === 200) {
            resultInfo(
               'success',
               t('employerFavouriteList.folderNameSavedSuccessTitle'),
               t('employerFavouriteList.close')
            );
            setShowFolderName(true);
         } else {
            Swal.fire({
               icon: 'error',
               text: response.data.message,
               title: t('employerFavouriteList.failedTitle'),
               confirmButtonText: t('employerFavouriteList.close'),
            });
         }

         getData();
      } catch (error) {
         setLoading(false);

         Swal.fire({
            icon: 'error',
            title: t('employerFavouriteList.failedTitle'),
            confirmButtonText: t('employerFavouriteList.close'),
         });

         console.error("Couldn't update the folder name!", error);
      }
   };

   const handleMoveResume = async (cvId) => {
      try {
         setLoading(true);

         const response = await axios.post(
            BaseApi + `/folder/add_cv/${cvId}`,
            { folder_id: selectedFolderId },
            {
               headers: {
                  key: ApiKey,
                  token: tokenKey,
                  contentType: 'application/json',
               },
            }
         );

         setLoading(false);

         if (response.status === 200) {
            resultInfo(
               'success',
               t('employerFavouriteList.resumeMovedSuccessTitle'),
               t('employerFavouriteList.close')
            );
            setIsDropdownOpen(false);
            setSelectedFolderId(null);
         } else {
            Swal.fire({
               icon: 'error',
               text: response.data.message,
               title: t('employerFavouriteList.failedTitle'),
               confirmButtonText: t('employerFavouriteList.close'),
            });
         }

         getData();
      } catch (error) {
         setLoading(false);

         Swal.fire({
            icon: 'error',
            title: t('employerFavouriteList.failedTitle'),
            confirmButtonText: t('employerFavouriteList.close'),
         });

         console.error("Couldn't update the folder name!", error);
      }
   };

   const handleFolderDelete = async () => {
      try {
         const confirmationResult = await Swal.fire({
            icon: 'question',
            showCancelButton: true,
            cancelButtonText: t('employerFavouriteList.no'),
            confirmButtonText: t('employerFavouriteList.yes'),
            text: t('employerFavouriteList.removeFolderConfirmTxt'),
            title: t('employerFavouriteList.removeFolderConfirmTitle'),
         });

         if (confirmationResult.isConfirmed) {
            setLoading(true);

            const response = await axios.get(BaseApi + `/folder/delete/${folderId}`, {
               headers: {
                  key: ApiKey,
                  token: tokenKey,
                  contentType: 'application/json',
               },
            });

            setLoading(false);

            if (response.status === 200) {
               resultInfo(
                  'success',
                  t('employerFavouriteList.removeFolderSuccessTitle'),
                  t('employerFavouriteList.close')
               );
               navigate('/user/favouritelist');
            } else {
               Swal.fire({
                  icon: 'error',
                  text: response.data.message,
                  confirmButtonText: t('employerFavouriteList.close'),
                  title: t('employerFavouriteList.removeFolderFailedTitle'),
               });
            }
         }
      } catch (error) {
         setLoading(false);

         Swal.fire({
            icon: 'error',
            confirmButtonText: t('employerFavouriteList.close'),
            text: t('employerFavouriteList.removeFolderFailedTxt'),
            title: t('employerFavouriteList.removeFolderFailedTitle'),
         });

         console.error("Couldn't delete the record!", error);
      }
   };

   const handleDelete = async (id) => {
      try {
         const confirmationResult = await Swal.fire({
            icon: 'question',
            showCancelButton: true,
            cancelButtonText: t('employerFavouriteList.no'),
            confirmButtonText: t('employerFavouriteList.yes'),
            text: t('employerFavouriteList.removeProfileConfirmTxt'),
            title: t('employerFavouriteList.removeProfileConfirmTitle'),
         });

         if (confirmationResult.isConfirmed) {
            setLoading(true);

            const response = await axios.post(
               BaseApi + `/candidates/deleteFavoriteList/${id}`,
               null,
               {
                  headers: {
                     'Content-Type': 'application/json',
                     key: ApiKey,
                     token: tokenKey,
                  },
               }
            );

            setLoading(false);

            if (response.data.status === 200) {
               resultInfo(
                  'success',
                  t('employerFavouriteList.removeSuccessTitle'),
                  t('employerFavouriteList.close')
               );
            } else if (response.data.status === 400) {
               navigate('/');

               Cookies.remove('fname');
               Cookies.remove('user_id');
               Cookies.remove('user_type');
               Cookies.remove('tokenClient');

               resultInfo('warning', response.data.message, t('employerFavouriteList.close'));
            } else {
               Swal.fire({
                  icon: 'error',
                  text: response.data.message,
                  title: t('employerFavouriteList.failedTitle'),
                  confirmButtonText: t('employerFavouriteList.close'),
               });
            }

            getData();
         }
      } catch (error) {
         setLoading(false);

         if (
            error.message === 'Network Error' ||
            error.response.data.message === 'Token has expired'
         ) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         } else {
            Swal.fire({
               icon: 'error',
               text: t('employerFavouriteList.removeFailedTxt'),
               confirmButtonText: t('employerFavouriteList.close'),
               title: t('employerFavouriteList.removeFailedTitle'),
            });
         }

         console.error("Couldn't delete the record!", error);
      }
   };

   useEffect(() => {
      if (userType === 'candidate' || !tokenKey) {
         navigate('/user/employerlogin');
      } else {
         getData();
         getFoldersList();

         window.scrollTo(0, 0);
      }
   }, [tokenKey, navigate, userType]); // eslint-disable-line

   return (
      <>
         <NavBar />
         <div className="container favouriteList">
            <div className="row">
               <div className="col-lg-3 col-md-3">
                  <Sidebar />
               </div>
               {loading ? (
                  <div className="loader-container"></div>
               ) : (
                  <>
                     <div className="col-lg-9 col-md-9 mb-5">
                        <div className="FLHeader">
                           <div className="mb-3 d-flex PageHeader">
                              <img src="/Images/employerSide/icon4color.png" alt="" />
                              <h3 className="mx-2">
                                 {t('employerFavouriteList.favouriteJobseeker')}
                              </h3>
                           </div>
                           <div className="mb-4">
                              <div className="mb-4 text-muted">
                                 <Link to="/user/favouritelist">
                                    {t('employerFavouriteList.favouriteJobseeker')}
                                 </Link>
                                 {' / '}
                                 {folderData?.name}
                              </div>
                           </div>
                           <div style={{ height: '38px' }}>
                              {showFolderName ? (
                                 <div className="d-flex align-items-center">
                                    <p className="fs-5">{folderData?.name}</p>
                                    <button
                                       className="ms-2 btn py-1 px-1"
                                       onClick={() => setShowFolderName(false)}
                                    >
                                       <EditIcon sx={{ color: '#818181' }} />
                                    </button>
                                    <button className="btn py-1 px-1" onClick={handleFolderDelete}>
                                       <DeleteIcon sx={{ color: '#818181' }} />
                                    </button>
                                 </div>
                              ) : (
                                 <div className="d-flex align-items-center">
                                    <input
                                       type="text"
                                       name="folderName"
                                       value={folderName}
                                       onChange={handleChange}
                                       className="form-control createFolderInput"
                                       placeholder={t('employerFavouriteList.folderName')}
                                    />
                                    <button
                                       disabled={!folderName}
                                       onClick={handleFolderNameSave}
                                       className="favouriteListFoldersButton mt-0 ms-2"
                                    >
                                       {t('employerFavouriteList.save')}
                                    </button>
                                    <button
                                       className="favouriteListFoldersButton mt-0 ms-2"
                                       onClick={() => {
                                          setShowFolderName(true);
                                          setFolderName(folderData?.name);
                                       }}
                                    >
                                       {t('employerFavouriteList.cancel')}
                                    </button>
                                 </div>
                              )}
                           </div>
                           <div className="PHBody mt-4">
                              <table className="table">
                                 <thead>
                                    <tr className="table-active TrFirst">
                                       <th className="" scope="col p-3">
                                          {t('employerFavouriteList.SNo.')}
                                       </th>
                                       <th className="" scope="col p-3">
                                          {t('employerFavouriteList.jobseekerName')}
                                       </th>
                                       <th className="" scope="col p-3">
                                          {t('employerFavouriteList.resumeName')}
                                       </th>
                                       <th className="" scope="col p-3">
                                          {t('employerFavouriteList.action')}
                                       </th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {currentData?.map((i, index) => (
                                       <tr key={index} className="TrDefoult">
                                          <td className="">{index + 1}</td>
                                          <td className="">
                                             {i?.resume?.first_name} {i?.resume?.last_name}
                                          </td>
                                          <td className="">{i.resume?.position}</td>
                                          <td className="APActionButton">
                                             <button
                                                className="btn-primary button1"
                                                onClick={() => {
                                                   if (i.from_cv_database) {
                                                      if (i.from_cv_database === 1) {
                                                         navigate(`/candidates/listing/${i.cv_id}`);
                                                      } else {
                                                         navigate(
                                                            `/candidates/profile/${i.slug}?cv_id=${i.cv_id}`
                                                         );
                                                      }
                                                   } else {
                                                      if (i.type === 'simple') {
                                                         navigate(
                                                            `/candidates/simple-cv/${i.cv_id}`
                                                         );
                                                      }
                                                      if (i.type === 'cv') {
                                                         navigate(
                                                            `/candidates/listing/${i.cv_id}`
                                                         );
                                                      }
                                                   }
                                                }}
                                             >
                                                <Tooltip
                                                   title={t('employerFavouriteList.viewResume')}
                                                >
                                                   <PersonIcon />
                                                </Tooltip>
                                             </button>
                                             <button
                                                type="button"
                                                data-bs-toggle="dropdown"
                                                aria-expanded={isDropdownOpen}
                                                className="btn-primary button1"
                                                onClick={() => {
                                                   setSelectedFolderId(null);
                                                   setIsDropdownOpen(!isDropdownOpen);
                                                }}
                                             >
                                                <Tooltip
                                                   title={t('employerFavouriteList.moveResume')}
                                                >
                                                   <DriveFileMoveIcon />
                                                </Tooltip>
                                             </button>
                                             <div className="p-4 dropdown-menu dropdown-menu-end">
                                                <div className="foldersList">
                                                   {foldersList?.map((folder) => (
                                                      <button
                                                         key={folder.id}
                                                         className={`px-1 foldersListItemButton ${folder.id === selectedFolderId && 'active'}`}
                                                         onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedFolderId(folder.id);
                                                         }}
                                                      >
                                                         {folder.name}
                                                      </button>
                                                   ))}
                                                </div>
                                                <button
                                                   className="btn button1"
                                                   disabled={!selectedFolderId}
                                                   onClick={() => handleMoveResume(i.cv_id)}
                                                >
                                                   {t('employerFavouriteList.moveResume')}
                                                </button>
                                             </div>
                                             <button
                                                className="btn-secondary button1"
                                                onClick={() => handleDelete(i.id)}
                                             >
                                                <Tooltip
                                                   title={t('employerFavouriteList.deleteResume')}
                                                >
                                                   <DeleteIcon />
                                                </Tooltip>
                                             </button>
                                          </td>
                                       </tr>
                                    ))}
                                 </tbody>
                              </table>
                           </div>
                        </div>
                        <div className="favListPagination">
                           <p className="text-muted empPaginationData">
                              {t('pagination.NoofRecords')}{' '}
                              {favouriteData?.length > 0
                                 ? indexOfFirstRecord + 1
                                 : indexOfFirstRecord}
                              -{Math.min(indexOfLastRecord, favouriteData?.length)} of{' '}
                              {favouriteData ? favouriteData?.length : favouriteData?.length}
                           </p>
                           <div className="d-flex justify-content-center empPaginationButton">
                              <button
                                 className="navButton1 me-2"
                                 disabled={currentPage === 1}
                                 onClick={() => handlePageChange(currentPage - 1)}
                              >
                                 {t('pagination.Prev')}
                              </button>
                              <button
                                 className="navButton1"
                                 onClick={() => handlePageChange(currentPage + 1)}
                                 disabled={
                                    favouriteData
                                       ? indexOfLastRecord >= favouriteData?.length
                                       : indexOfLastRecord >= favouriteData?.length
                                 }
                              >
                                 {t('pagination.Next')}
                              </button>
                           </div>
                        </div>
                     </div>
                  </>
               )}
            </div>
         </div>

         <Footer />
      </>
   );
};

export default FolderPage;
